import { useEffect, useRef, useState } from "react";
import { BarcodeFormat } from "../../../utils/helpers/validation";
import { detectBarcodeType, isValidBarcodeCharacter } from "../../../utils/helpers/validation";

type BarcodeScannerProps = {
  onScan: (barcode: string, format: BarcodeFormat) => Promise<void>;
  scanTimeout?: number;
  minLength?: number;
};

const BarcodeScanner = ({ onScan, scanTimeout = 500, minLength = 5 }: BarcodeScannerProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const bufferRef = useRef("");
  const timerRef = useRef<number | null>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const resetBufferSoon = () => {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        bufferRef.current = "";
      }, scanTimeout);
    };

    const shouldIgnoreTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;

      return (
        el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.tagName === "SELECT" ||
        el.isContentEditable
      );
    };

    const onKeyDown = async (event: KeyboardEvent) => {
      if (loadingRef.current) return;
      if (shouldIgnoreTarget(event.target)) return;

      if (event.key.length > 1 && event.key !== "Enter") return;

      if (event.key === "Enter") {
        const code = bufferRef.current;
        bufferRef.current = "";
        clearTimer();

        if (code.length >= minLength) {
          const format = detectBarcodeType(code);
          try {
            setIsLoading(true);
            await onScan(code, format);
          } finally {
            setIsLoading(false);
          }
        }
        return;
      }

      if (isValidBarcodeCharacter(event.key)) {
        bufferRef.current += event.key;
        resetBufferSoon();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearTimer();
      bufferRef.current = "";
    };
  }, [onScan, scanTimeout, minLength]);

  return null;
};

export default BarcodeScanner;
